/**
 * Shared GLSL chunks for ctx.graphics.createShaderQuad.
 *
 * Every wallpaper we shipped re-declared the same `hash21` / `valueNoise` /
 * `fbm`, and each copy drifted: Alpine Ridge's star field was a `floor()` cell
 * test with no sub-cell position and no falloff, so every star rendered as a
 * flat filled square. One canonical copy, prepended by the host exactly like
 * the prelude, removes both the duplication and that class of drift.
 *
 * CONTEXT IS WebGL2. three >= 0.163 (we pin ^0.175) creates a WebGL2 context
 * and nothing here forces WebGL1. These chunks are AUTHORED in GLSL ES 1.00
 * SYNTAX — `varying`, `texture2D`, `gl_FragColor` — because createShaderQuad
 * builds a plain ShaderMaterial with no `glslVersion`, and three then
 * transpiles ES 1.00 source to ES 3.00 for the real compile. So the syntax is
 * ES 1.00 but the CAPABILITIES are WebGL2:
 *   - derivatives (`fwidth`, `dFdx`, `dFdy`) ARE available — no extension
 *     pragma needed, and check-shaders.mjs already allows them;
 *   - dynamic loop bounds and non-constant indexing ARE allowed;
 *   - float/half-float textures ARE filterable and renderable.
 * What is still off-limits is ES-3.00-ONLY SYNTAX (`texelFetch`, `in`/`out`,
 * `textureLod`, layout qualifiers): three's transpiler rewrites ES 1.00 for
 * you, it does not accept a mix. Those need `glslVersion: GLSL3`, which
 * createShaderQuad does not expose.
 *
 * The RESERVED-WORD hazard is real and unchanged: `patch`, `sample`, `input`,
 * `output`, `filter` and friends are reserved in GLSL and using one as an
 * identifier kills the whole shader silently.
 *
 * Chunk text is parsed by scripts/check-shaders.mjs too, which re-reads THIS
 * file — keep each chunk as `const CHUNK_<NAME> = \`…\`;` so that stays true.
 */

const CHUNK_HASH = `
float hash11(float n){ return fract(sin(n) * 43758.5453123); }
float hash21(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
`;

const CHUNK_NOISE = `
/* Bilinear value noise with a smoothstep fade — the cheap workhorse. */
float valueNoise(vec2 p){
  vec2 i = floor(p);
  vec2 f = fract(p);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
`;

const CHUNK_FBM = `
/* 5 octaves of value noise. Roughly half a wallpaper's per-frame budget —
   two or three calls per pixel is the sane ceiling behind a live desktop. */
float fbm(vec2 p){
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * valueNoise(p);
    p *= 2.03;
    a *= 0.5;
  }
  return v;
}
`;

const CHUNK_STARS = `
/* Star field: at most one star per grid cell, placed at a RANDOM point inside
   the cell and drawn with a smooth radial falloff, so it reads as a round
   point light rather than the cell it lives in. Twinkle is phase-shifted per
   star off its own hash. Call it at 2-3 scales for depth.
     p     aspect-corrected position (vec2(uv.x * aspect, uv.y))
     scale cells per unit — bigger is denser and finer
     t     time (scale it per layer so the layers twinkle out of sync)
     seed  decorrelates layers that share a scale */
float stars(vec2 p, float scale, float t, float seed){
  vec2 q = p * scale;
  vec2 i = floor(q);
  vec2 f = fract(q);
  float h = hash21(i + seed);
  float keep = step(0.86, h);
  vec2 c = vec2(hash21(i + 5.3), hash21(i + 9.1));
  float d = length(f - c);
  float twinkle = 0.55 + 0.45 * sin(t * 2.0 + h * 60.0);
  return keep * smoothstep(0.06, 0.0, d) * twinkle;
}
`;

/** Canonical emit order — a chunk may only depend on chunks before it. */
export const GLSL_CHUNK_ORDER = ["hash", "noise", "fbm", "stars"] as const;

export type GlslChunkName = (typeof GLSL_CHUNK_ORDER)[number];

/** Source text of each chunk, keyed by name. */
export const GLSL_CHUNKS: Record<GlslChunkName, string> = {
  hash: CHUNK_HASH,
  noise: CHUNK_NOISE,
  fbm: CHUNK_FBM,
  stars: CHUNK_STARS,
};

/** What each chunk needs declared before it. Resolved transitively. */
const GLSL_CHUNK_DEPS: Record<GlslChunkName, GlslChunkName[]> = {
  hash: [],
  noise: ["hash"],
  fbm: ["noise"],
  stars: ["hash"],
};

/**
 * Expand `names` with their dependencies, de-duplicate, and return the source
 * in canonical order. Unknown names are ignored rather than thrown: a module
 * built against a newer SDK should degrade to a missing helper (a compile error
 * the host reports through onError) rather than fail to mount at all.
 */
export function resolveGlslChunks(names: readonly string[] | undefined): string {
  if (!names || !names.length) return "";
  const want = new Set<GlslChunkName>();
  const add = (n: string) => {
    if (!(n in GLSL_CHUNKS)) return;
    const name = n as GlslChunkName;
    if (want.has(name)) return;
    want.add(name);
    for (const dep of GLSL_CHUNK_DEPS[name]) add(dep);
  };
  for (const n of names) add(n);
  return GLSL_CHUNK_ORDER.filter((n) => want.has(n))
    .map((n) => GLSL_CHUNKS[n])
    .join("");
}
